import { useState, useCallback } from 'react';
import { getCacheManager } from '../../../utils/cache-service';
import { performMigration, getMigrationStatus, type MigrationStatus } from '../../../utils/cache-migration';
import type { MessageState } from '../types';
import type { IndexedDBCache } from '../../../utils/indexeddb-cache';

export function useCacheManagement(
  showMessage: (type: MessageState['type'], text: string) => void
) {

  const [cacheStats, setCacheStats] = useState<Awaited<ReturnType<IndexedDBCache["getStats"]>> | null>(null);
  const [migrationStatus, setMigrationStatus] = useState<MigrationStatus | null>(null);
  const [isMigrating, setIsMigrating] = useState(false);

  const loadCacheStats = useCallback(async () => {
    try {
      const cacheManager = getCacheManager();
      const stats = await cacheManager.getStats();
      setCacheStats(stats);
    } catch (error) {
      console.error('Failed to load cache stats:', error);
    }
  }, []);

  const loadMigrationStatus = useCallback(() => {
    try {
      const status = getMigrationStatus();
      setMigrationStatus(status);
    } catch (error) {
      console.error('Failed to load migration status:', error);
    }
  }, []);

  const handleMigration = useCallback(async () => {
    setIsMigrating(true);
    try {
      const result = await performMigration();
      setMigrationStatus(result);

      if (result.isCompleted) {
        showMessage('success', `Migration completed: ${result.migratedEntries} entries migrated`);
        await loadCacheStats();
      } else if (result.errors.length > 0) {
        showMessage('error', `Migration failed: ${result.errors[0]}`);
      }
    } catch (error) {
      showMessage('error', `Migration error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsMigrating(false);
    }
  }, [loadCacheStats, showMessage]);

  const handleClearExpired = useCallback(async () => {
    try {
      const cacheManager = getCacheManager();
      const clearedCount = await cacheManager.clearExpired();
      showMessage('info', `Cleared ${clearedCount} expired cache entries`);
      await loadCacheStats();
    } catch (error) {
      showMessage('error', `Failed to clear expired cache: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }, [loadCacheStats, showMessage]);

  const handleClearAll = useCallback(async () => {
    if (window.confirm('Clear all cache data? This may slow down the next data load.')) {
      try {
        const cacheManager = getCacheManager();
        const clearedCount = await cacheManager.clearAll();
        showMessage('info', `Cleared all cache: ${clearedCount} entries removed`);
        await loadCacheStats();
      } catch (error) {
        showMessage('error', `Failed to clear cache: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
  }, [loadCacheStats, showMessage]);

  const handleClearServiceCache = useCallback(async (service: 'openai' | 'anthropic' | 'gemini' | 'github') => {
    if (window.confirm(`Clear ${service.toUpperCase()} cache entries?`)) {
      try {
        const cacheManager = getCacheManager();
        const clearedCount = await cacheManager[service].clearCache();
        showMessage('info', `Cleared ${service.toUpperCase()} cache: ${clearedCount} entries removed`);
        await loadCacheStats();
      } catch (error) {
        showMessage('error', `Failed to clear ${service} cache: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
  }, [loadCacheStats, showMessage]);

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  return {
    cacheStats,
    migrationStatus,
    isMigrating,
    loadCacheStats,
    loadMigrationStatus,
    handleMigration,
    handleClearExpired,
    handleClearAll,
    handleClearServiceCache,
    formatBytes
  };
}
